import { useMemo, useState } from 'react'

export type SortDirection = 'asc' | 'desc' | null

export function useSortableData<T>(
  data: T[],
  getSortValue: (
    item: T,
    sortKey: keyof T
  ) => string | number | null | undefined
) {
  const [sortKey, setSortKey] = useState<keyof T | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)

  const sortedData = useMemo(() => {
    if (!sortKey || !sortDirection) return data

    return [...data].sort((a, b) => {
      const aValue = getSortValue(a, sortKey)
      const bValue = getSortValue(b, sortKey)

      if (
        (aValue === null || aValue === undefined) &&
        bValue !== null &&
        bValue !== undefined
      ) {
        return 1
      }

      if (
        (bValue === null || bValue === undefined) &&
        aValue !== null &&
        aValue !== undefined
      ) {
        return -1
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
      }

      return 0
    })
  }, [data, sortKey, sortDirection, getSortValue])

  const handleSort = (key: keyof T) => {
    if (key !== sortKey) {
      setSortKey(key)
      setSortDirection('asc')
      return
    }

    setSortDirection((prev) => {
      if (prev === 'asc') return 'desc'
      if (prev === 'desc') return null
      return 'asc'
    })
  }

  return {
    sortedData,
    sortKey,
    sortDirection,
    handleSort
  }
}
